import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  Stack,
  Pagination,
  Chip,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useAuth } from '../../../contexts/AuthContext';
import ImageViewerDialog from '../ImageViewerDialog';
import { createConcurrencyLimiter } from '../../../utils/concurrentLimit';

interface ContainerEvent {
  id: string;
  sightingId: string;
  deviceEventId: string;
  cameraDeviceId: string;
  cameraName: string;
  state: 'open' | 'closed' | 'undocked';
  docked: boolean;
  inferred: boolean;
  seen: string;
  code: string | null;
  valid: boolean | null;
  confirmed: boolean;
  hasFrame: boolean;
  // The docking's pictures of its number: the number itself, and the image it was read in
  hasNumberCrop: boolean;
  hasNumberFrame: boolean;
}

interface ContainerEventsTabProps {
  deviceId: string;
  includeChildren?: boolean;
}

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const toUtcIso = (value: string): string => {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString();
};

// The event's own picture; with `kind`, a picture of its container's number instead
const eventImageUrl = (e: ContainerEvent, maxWidth?: number, kind?: 'frame' | 'crop') => {
  const params = new URLSearchParams();
  if (maxWidth) params.append('maxWidth', maxWidth.toString());
  if (kind) params.append('kind', kind);
  const query = params.toString();
  return `/api/devices/${e.cameraDeviceId}/container-events/${e.id}/image${query ? `?${query}` : ''}`;
};

const eventLabel = (e: ContainerEvent): { label: string; color: 'warning' | 'primary' | 'default' } => {
  if (e.docked) {
    return e.state === 'open' ? { label: 'Docked (open)', color: 'warning' } : { label: 'Docked (closed)', color: 'primary' };
  }
  if (e.state === 'open') return { label: 'Doors opened', color: 'warning' };
  if (e.state === 'closed') return { label: 'Doors closed', color: 'primary' };
  if (e.inferred) return { label: 'Undocked (not seen)', color: 'default' };
  return { label: 'Undocked', color: 'default' };
};

const checkMark = (e: ContainerEvent) => {
  if (!e.code) return null;
  if (e.confirmed) {
    return <Tooltip title="Check digit passes"><CheckCircleIcon color="success" fontSize="small" /></Tooltip>;
  }
  if (e.valid === true) {
    return <Tooltip title="Check digit passes, but too few reads to confirm"><CheckCircleOutlineIcon color="success" fontSize="small" /></Tooltip>;
  }
  if (e.valid === false) {
    return <Tooltip title="Check digit fails"><ErrorOutlineIcon color="error" fontSize="small" /></Tooltip>;
  }
  return <Tooltip title="Read without its check digit"><HelpOutlineIcon color="warning" fontSize="small" /></Tooltip>;
};

const ContainerEventsTab: React.FC<ContainerEventsTabProps> = ({ deviceId, includeChildren = false }) => {
  const auth = useAuth();
  const accessToken = auth.tokens?.accessToken;
  const [events, setEvents] = useState<ContainerEvent[]>([]);
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
  // The number as read, by docking (sightingId): a docking's events share it
  const [crops, setCrops] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeSearch, setCodeSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [total, setTotal] = useState(0);

  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerUrl, setViewerUrl] = useState('');
  const [viewerTitle, setViewerTitle] = useState('');

  const [deleteAllConfirmOpen, setDeleteAllConfirmOpen] = useState(false);
  const [deletePageConfirmOpen, setDeletePageConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isAdmin = auth.hasRole('DMO_ADMIN');

  const authHeader = { Authorization: `Bearer ${accessToken}` };

  // The object URLs now held, for the unmount cleanup (which would otherwise see only the first render's)
  const objectUrls = useRef<string[]>([]);
  objectUrls.current = [...Object.values(thumbnails), ...Object.values(crops), ...(viewerUrl ? [viewerUrl] : [])];

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        skip: ((page - 1) * pageSize).toString(),
        take: pageSize.toString(),
      });
      if (codeSearch.trim()) params.append('code', codeSearch.trim());
      const startUtc = toUtcIso(startDate);
      const endUtc = toUtcIso(endDate);
      if (startUtc) params.append('startDate', startUtc);
      if (endUtc) params.append('endDate', endUtc);
      if (includeChildren) params.append('includeChildren', 'true');

      const res = await fetch(`/api/devices/${deviceId}/container-events?${params}`, { headers: authHeader });
      if (!res.ok) throw new Error('Failed to load container events');
      const data = await res.json();
      const rows: ContainerEvent[] = data.data || [];
      setEvents(rows);
      setTotal(data.total || 0);

      // Fetch small thumbnails for events that have a frame, and the number as read once per docking
      const limit = createConcurrencyLimiter(8);
      const fetchImages = async (items: ContainerEvent[], key: (e: ContainerEvent) => string, url: (e: ContainerEvent) => string) => {
        const results = await Promise.allSettled(
          items.map((e) => limit(async () => {
            const r = await fetch(url(e), { headers: authHeader });
            if (!r.ok) return null;
            return [key(e), URL.createObjectURL(await r.blob())] as const;
          }))
        );
        const next: Record<string, string> = {};
        results.forEach((r) => {
          if (r.status === 'fulfilled' && r.value) next[r.value[0]] = r.value[1];
        });
        return next;
      };
      const numbered = rows.filter((e, i) => e.hasNumberCrop && rows.findIndex((o) => o.sightingId === e.sightingId) === i);
      const [frames, numbers] = await Promise.all([
        fetchImages(rows.filter((e) => e.hasFrame), (e) => e.id, (e) => eventImageUrl(e, 80)),
        fetchImages(numbered, (e) => e.sightingId, (e) => eventImageUrl(e, 240, 'crop')),
      ]);
      setThumbnails((prev) => {
        Object.values(prev).forEach((u) => URL.revokeObjectURL(u));
        return frames;
      });
      setCrops((prev) => {
        Object.values(prev).forEach((u) => URL.revokeObjectURL(u));
        return numbers;
      });
    } catch (e: any) {
      setError(e.message || 'Failed to load container events');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, includeChildren, page, pageSize, codeSearch, startDate, endDate, accessToken]);

  useEffect(() => {
    const t = setTimeout(loadEvents, 300);
    return () => clearTimeout(t);
  }, [loadEvents]);

  useEffect(() => () => {
    objectUrls.current.forEach((u) => URL.revokeObjectURL(u));
  }, []);

  // An event can't be deleted on its own: it goes with its docking (the container's whole stay,
  // several rows, some perhaps on other pages)
  const handleDeletePage = async () => {
    setDeleting(true);
    setError(null);
    try {
      const seen = new Set<string>();
      const unique = events.filter((e) => {
        if (seen.has(e.deviceEventId)) return false;
        seen.add(e.deviceEventId);
        return true;
      });
      const limit = createConcurrencyLimiter(6);
      const results = await Promise.allSettled(
        unique.map(({ deviceEventId, cameraDeviceId }) => limit(async () => {
          const res = await fetch(`/api/devices/${cameraDeviceId}/events/${deviceEventId}`, {
            method: 'DELETE', headers: authHeader,
          });
          if (!res.ok) throw new Error('Delete failed');
        }))
      );
      const failedCount = results.filter((r) => r.status === 'rejected').length;
      if (failedCount > 0) setError(`Failed to delete ${failedCount} of ${unique.length} docking(s)`);
      if (page > 1) setPage(1); else await loadEvents();
    } catch {
      setError('Failed to delete this page of container events');
    } finally {
      setDeleting(false);
      setDeletePageConfirmOpen(false);
    }
  };

  const handleDeleteAll = async () => {
    setDeleting(true);
    setError(null);
    try {
      const res = await fetch(`/api/devices/${deviceId}/events/all?eventType=container&includeChildren=true`, {
        method: 'DELETE', headers: authHeader,
      });
      if (!res.ok) throw new Error(res.status === 403 ? 'Only administrators can delete all container events' : 'Failed to delete all container events');
      if (page > 1) setPage(1); else await loadEvents();
    } catch (e: any) {
      setError(e.message || 'Failed to delete all container events');
    } finally {
      setDeleting(false);
      setDeleteAllConfirmOpen(false);
    }
  };

  const openImage = async (url: string, title: string) => {
    try {
      const r = await fetch(url, { headers: authHeader });
      if (!r.ok) throw new Error();
      if (viewerUrl) URL.revokeObjectURL(viewerUrl);
      setViewerUrl(URL.createObjectURL(await r.blob()));
      setViewerTitle(title);
      setViewerOpen(true);
    } catch {
      setError('Failed to load image');
    }
  };

  const openFrame = (e: ContainerEvent) => {
    if (!e.hasFrame) return;
    const { label } = eventLabel(e);
    openImage(eventImageUrl(e), `${e.code || 'Unread'} — ${label} — ${e.cameraName} — ${new Date(e.seen).toLocaleString()}`);
  };

  // The image the number was read in, the number boxed; the number alone if that's all there is
  const openNumber = (e: ContainerEvent) => {
    openImage(eventImageUrl(e, undefined, e.hasNumberFrame ? 'frame' : 'crop'), `${e.code || 'Unread'} — number as read — ${e.cameraName}`);
  };

  const pageCount = Math.ceil(total / pageSize);

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          label="Container number"
          size="small"
          value={codeSearch}
          onChange={(e) => { setCodeSearch(e.target.value.toUpperCase()); setPage(1); }}
          placeholder="e.g. MSKU1234565"
        />
        <TextField
          label="Start Date"
          type="date"
          size="small"
          value={startDate}
          onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="End Date"
          type="date"
          size="small"
          value={endDate}
          onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          select
          label="Per page"
          size="small"
          value={pageSize}
          onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
          sx={{ minWidth: 100 }}
        >
          {PAGE_SIZE_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
        </TextField>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {total} event{total !== 1 ? 's' : ''}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<DeleteIcon />}
            disabled={deleting || events.length === 0}
            onClick={() => setDeletePageConfirmOpen(true)}
          >
            Delete Page
          </Button>
          {isAdmin && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              disabled={deleting || total === 0}
              onClick={() => setDeleteAllConfirmOpen(true)}
            >
              Delete All
            </Button>
          )}
        </Stack>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 90 }} />
                <TableCell>Number</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>Camera</TableCell>
                <TableCell>Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary" py={2}>No container events</Typography>
                  </TableCell>
                </TableRow>
              ) : events.map((e) => {
                const { label, color } = eventLabel(e);
                return (
                  <TableRow key={e.id} hover>
                    <TableCell sx={{ width: 90, p: 0.5 }}>
                      {thumbnails[e.id] ? (
                        <Box
                          component="img"
                          src={thumbnails[e.id]}
                          alt={label}
                          sx={{ width: 80, height: 54, objectFit: 'cover', cursor: 'pointer', display: 'block', borderRadius: 1 }}
                          onClick={() => openFrame(e)}
                        />
                      ) : e.hasFrame ? (
                        <Box
                          sx={{ width: 80, height: 54, bgcolor: 'action.hover', borderRadius: 1, cursor: 'pointer' }}
                          onClick={() => openFrame(e)}
                        />
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {e.code ? (
                          <Typography variant="body2" fontWeight={600}>{e.code}</Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">Unread</Typography>
                        )}
                        {checkMark(e)}
                        {crops[e.sightingId] && (
                          <Tooltip title="The number as read. Click for the image it was read in.">
                            <Box
                              component="img"
                              src={crops[e.sightingId]}
                              alt={e.code || 'Container number'}
                              sx={{ height: 32, maxWidth: 140, ml: 1, cursor: 'pointer', display: 'block', borderRadius: 0.5 }}
                              onClick={() => openNumber(e)}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {e.inferred ? (
                        <Tooltip title="The truck drove off unseen; the dock was never seen empty">
                          <Chip size="small" variant="outlined" label={label} />
                        </Tooltip>
                      ) : (
                        <Chip
                          size="small"
                          color={color === 'default' ? undefined : color}
                          variant={color === 'default' ? 'outlined' : 'filled'}
                          label={label}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{e.cameraName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {new Date(e.seen).toLocaleString()}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {pageCount > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination count={pageCount} page={page} onChange={(_, p) => setPage(p)} />
        </Box>
      )}

      <ImageViewerDialog
        open={viewerOpen}
        imageUrl={viewerUrl}
        eventName={viewerTitle}
        onClose={() => setViewerOpen(false)}
      />

      <Dialog open={deletePageConfirmOpen} onClose={() => setDeletePageConfirmOpen(false)}>
        <DialogTitle>Delete This Page</DialogTitle>
        <DialogContent>
          <Typography>
            Delete the dockings shown on this page? Each goes with all of its events (docked, doors, undocked),
            including any on other pages. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePageConfirmOpen(false)}>Cancel</Button>
          <Button color="error" onClick={handleDeletePage} disabled={deleting}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteAllConfirmOpen} onClose={() => setDeleteAllConfirmOpen(false)}>
        <DialogTitle>Delete All Container Events</DialogTitle>
        <DialogContent>
          <Typography>
            Delete every container event for this device and its cameras, whatever the filters above? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAllConfirmOpen(false)}>Cancel</Button>
          <Button color="error" onClick={handleDeleteAll} disabled={deleting}>Delete All</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContainerEventsTab;
